import { useState } from "react";
import { Button, Input } from "@/components";
import { FormCard } from "@/components/FormCard";
import axiosInstance from "@/lib/axios";
import { ArrowRight, Copy, CopyCheck, Zap } from "lucide-react";

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export default function WhatsAppWidgetGenerator() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [brandName, setBrandName] = useState("");
  const [buttonPosition, setButtonPosition] = useState("bottom-left");
  const [welcomeText, setWelcomeText] = useState("Hello! How can we help?");
  const [prefilledMessage, setPrefilledMessage] = useState(
    "Hello! I'd like to know more about your services.",
  );
  const [openWidgetDefault, setOpenWidgetDefault] = useState(false);
  const [brandImageUrl, setBrandImageUrl] = useState("");

  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [message, setMessage] = useState("Chat with us 🚀");
  const [buttonColor, setButtonColor] = useState("#185db8");
  const [copied, setCopied] = useState(false);
  const [generatedCode, setGeneratedCode] = useState("");
  const [email, setEmail] = useState("");

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) return false;

    return true;
  };

  const generateCode = () => {
    let isValid = true;
    if (!phoneNumber.trim()) {
      setPhoneError("Phone number is required");
      isValid = false;
    } else {
      setPhoneError("");
    }

    if (!email.trim()) {
      setEmailError("Business email is required");
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Enter a valid business email");
      isValid = false;
    } else {
      setEmailError("");
    }

    if (!isValid) return;

    const position = buttonPosition === "bottom-left" ? "left" : "right";
    const marginRight = buttonPosition === "bottom-left" ? "0" : "20";
    const marginLeft = buttonPosition === "bottom-left" ? "20" : "0";

    const code = `<script>
  var url = 'https://quick-assists-media.s3.ap-south-1.amazonaws.com/whatsapp-wati-script/Widget.js';
  var s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.src = url;
  var options = {
    "enabled": true,
    "chatButtonSetting": {
      "backgroundColor": "${buttonColor}",
      "ctaText": "${message}",
      "borderRadius": "25",
      "marginLeft": "${marginLeft}",
      "marginRight": "${marginRight}",
      "marginBottom": "20",
      ctaIconWATI: false,
      "position": "${position}"
    },
    "brandSetting": {
      "brandName": "${brandName}",
      "brandSubTitle": "undefined",
      "brandImg": "${brandImageUrl}",
      "welcomeText": "${welcomeText.replace(/\n/g, "\\n")}",
      "messageText": "${prefilledMessage}",
      "backgroundColor": "${buttonColor}",
      "ctaText": "${message}",
      "borderRadius": "25",
      "autoShow": ${openWidgetDefault},
      "phoneNumber": "${phoneNumber.trim()}"
    }
  };
  s.onload = function() {
    CreateWhatsappChatWidget(options);
  };
  var x = document.getElementsByTagName('script')[0];
  x.parentNode.insertBefore(s, x);
<\/script>`;

    setGeneratedCode(code);

    setIsGenerating(true);
    axiosInstance
      .post("/free-tools/submit", {
        toolType: "whatsapp-widget",
        type: 2,
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
        config: {
          buttonColor,
          buttonLabel: message,
          brandName,
          brandImageUrl,
          welcomeText,
          messageText: prefilledMessage,
          position,
          autoShow: openWidgetDefault,
        },
      })
      .catch((err) => console.error("Failed to capture lead:", err))
      .finally(() => setIsGenerating(false));
  };

  const copyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        <div className="grid lg:grid-cols-2 gap-4">
        {/* Section 1: Button Style */}
        <FormCard title="Button style" description="Choose how the launcher looks and behaves">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Brand Color */}
            <div className="space-y-1.5">
              <label htmlFor="brand-color-text" className="block text-xs font-semibold text-ui-body">Brand color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={buttonColor}
                  onChange={(e) => setButtonColor(e.target.value)}
                  className="w-9 h-9 rounded-sm border border-ui-hairline cursor-pointer shrink-0"
                />
                <Input
                  id="brand-color-text"
                  type="text"
                  value={buttonColor}
                  onChange={(e) => setButtonColor(e.target.value)}
                  placeholder="#185db8"
                  className="flex-1 h-9 text-xs"
                />
              </div>
            </div>

            {/* Button Label / CTA Text */}
            <div className="space-y-1.5">
              <label htmlFor="button-label" className="block text-xs font-semibold text-ui-body">Button label</label>
              <Input
                id="button-label"
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Chat with us"
                className="w-full h-9 text-xs"
              />
            </div>

            {/* Position */}
            <div className="space-y-2">
              <span className="block text-xs font-semibold text-ui-body">Position</span>
              <div className="flex items-center gap-1.5 p-1 bg-ui-surface-soft border border-ui-hairline rounded-sm w-fit">
                <button
                  type="button"
                  onClick={() => setButtonPosition("bottom-left")}
                  className={cn(
                    "px-3 py-1.5 text-xs font-bold rounded-sm transition-all whitespace-nowrap",
                    buttonPosition === "bottom-left"
                      ? "bg-ui-surface-card text-ui-primary border border-ui-hairline shadow-sm"
                      : "text-ui-muted hover:text-ui-ink"
                  )}
                >
                  Bottom left
                </button>
                <button
                  type="button"
                  onClick={() => setButtonPosition("bottom-right")}
                  className={cn(
                    "px-3 py-1.5 text-xs font-bold rounded-sm transition-all whitespace-nowrap",
                    buttonPosition === "bottom-right"
                      ? "bg-ui-surface-card text-ui-primary border border-ui-hairline shadow-sm"
                      : "text-ui-muted hover:text-ui-ink"
                  )}
                >
                  Bottom right
                </button>
              </div>
            </div>

            {/* Opens / Default */}
            <div className="space-y-2">
              <span className="block text-xs font-semibold text-ui-body">Opens</span>
              <div className="flex items-center gap-1.5 p-1 bg-ui-surface-soft border border-ui-hairline rounded-sm w-fit">
                <button
                  type="button"
                  onClick={() => setOpenWidgetDefault(false)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-bold rounded-sm transition-all whitespace-nowrap",
                    !openWidgetDefault
                      ? "bg-ui-surface-card text-ui-primary border border-ui-hairline shadow-sm"
                      : "text-ui-muted hover:text-ui-ink"
                  )}
                >
                  On click
                </button>
                <button
                  type="button"
                  onClick={() => setOpenWidgetDefault(true)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-bold rounded-sm transition-all whitespace-nowrap",
                    openWidgetDefault
                      ? "bg-ui-surface-card text-ui-primary border border-ui-hairline shadow-sm"
                      : "text-ui-muted hover:text-ui-ink"
                  )}
                >
                  Automatically
                </button>
              </div>
            </div>
          </div>
        </FormCard>

        {/* Section 2: Business details */}
        <FormCard title="Business details" description="Shown to visitors inside the widget">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Brand Name */}
            <div className="space-y-1.5">
              <label htmlFor="brand-name" className="block text-xs font-semibold text-ui-body">Brand name</label>
              <Input
                id="brand-name"
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Your company name"
                className="w-full h-9 text-xs"
              />
            </div>

            {/* Contact email */}
            <div className="space-y-1.5">
              <label htmlFor="email-id" className="block text-xs font-semibold text-ui-body">Contact email</label>
              <Input
                id="email-id"
                type="email"
                value={email}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);
                  if (!value.trim()) {
                    setEmailError("Business email is required");
                  } else if (!validateEmail(value)) {
                    setEmailError("Enter a valid business email");
                  } else {
                    setEmailError("");
                  }
                }}
                placeholder="business@company.com"
                className="w-full h-9 text-xs"
              />
              {emailError && (
                <p className="text-[10px] text-ui-error font-medium">{emailError}</p>
              )}
            </div>

            {/* Phone Number */}
            <div className="space-y-1.5">
              <label htmlFor="phone-number" className="block text-xs font-semibold text-ui-body flex items-center justify-between">
                Phone number
                <span className="block text-[10px] text-ui-muted font-normal mt-0.5">Include country code</span>
              </label>
              <Input
                id="phone-number"
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  setPhoneNumber(value);
                  if (value.trim()) {
                    setPhoneError("");
                  }
                }}
                placeholder="1234567890"
                className="w-full h-9 text-xs"
              />
              {phoneError && (
                <p className="text-[10px] text-ui-error font-medium">{phoneError}</p>
              )}
            </div>

            {/* Logo URL */}
            <div className="space-y-1.5">
              <label htmlFor="brand-image-url" className="block text-xs font-semibold text-ui-body">Logo URL</label>
              <Input
                id="brand-image-url"
                type="url"
                value={brandImageUrl}
                onChange={(e) => setBrandImageUrl(e.target.value)}
                placeholder="https://yoursite.com/logo.png"
                className="w-full h-9 text-xs"
              />
            </div>
          </div>
        </FormCard>
</div>
        {/* Section 3: Messages */}
        <FormCard title="Messages" description="What visitors read and send first">
          <div className="space-y-4">
            {/* Welcome Text */}
            <div className="space-y-1.5">
              <label htmlFor="welcome-text" className="block text-xs font-semibold text-ui-body">
                Welcome text
                <span className="block text-[10px] text-ui-muted font-normal mt-0.5">{welcomeText.length}/40 characters</span>
              </label>
              <Input
                id="welcome-text"
                type="text"
                value={welcomeText}
                onChange={(e) => setWelcomeText(e.target.value)}
                placeholder="Hello! How can we help?"
                maxLength={40}
                className="w-full h-9 text-xs"
              />
            </div>

            {/* Pre-filled Message */}
            <div className="space-y-1.5">
              <label htmlFor="prefilled-message" className="block text-xs font-semibold text-ui-body flex justify-between items-center">
                Pre-filled message
                <span className="block text-[10px] text-ui-muted font-normal mt-0.5">Optional — auto-sent when a visitor opens the chat</span>
              </label>
              <textarea
                id="prefilled-message"
                value={prefilledMessage}
                onChange={(e) => setPrefilledMessage(e.target.value)}
                placeholder="Hello! I'd like to know more about your services."
                rows={2}
                className="w-full px-3 py-2 border border-ui-hairline rounded-sm focus:ring-1 focus:ring-ui-primary focus:border-ui-primary bg-ui-surface-card text-xs text-ui-ink resize-none custom-scrollbar"
              />
            </div>
          </div>
        </FormCard>

        {/* Action Button Row */}
        <div className="flex justify-end pt-2">
          <Button
            size="sm"
            onClick={generateCode}
            loading={isGenerating}
            className="flex items-center gap-2 h-9 text-xs font-semibold"
          >
            Generate Widget Code
            <ArrowRight size={14} />
          </Button>
        </div>
      </div>

      {/* Generated Code Output */}
      {generatedCode && (
        <div className="flex flex-col justify-between w-full gap-4 bg-ui-surface-card p-4 rounded-sm border border-ui-hairline mt-4">
          <div className="flex items-center justify-between pb-2 border-b border-ui-hairline-soft">
            <h3 className="text-xs font-bold text-ui-ink">Your Generated Widget Code</h3>
            <button
              type="button"
              onClick={copyCode}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-sm font-semibold transition-all text-xs",
                copied
                  ? "bg-ui-success text-white"
                  : "bg-ui-primary text-white hover:bg-ui-primary-active"
              )}
            >
              {copied ? (
                <>
                  <CopyCheck size={14} strokeWidth={2.5} />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={14} />
                  <span>Copy Code</span>
                </>
              )}
            </button>
          </div>

          <pre className="text-ui-primary text-xs overflow-x-auto whitespace-pre-wrap break-all p-3 bg-ui-surface-soft border border-ui-hairline rounded-sm font-mono custom-scrollbar max-h-40">
            <code>{generatedCode}</code>
          </pre>

          <div className="rounded-sm border border-ui-warning/30 bg-ui-warning/10 p-3 text-xs text-ui-warning flex gap-2">
            <span className="font-bold">Note:</span> Paste this code before the closing &lt;/body&gt; tag on your website.
          </div>

          <div className="divider flex items-center gap-2 my-1">
            <hr className="flex-1 border-t border-ui-hairline-soft" />
            <span className="text-[10px] font-bold text-ui-muted whitespace-nowrap uppercase tracking-wider">OR</span>
            <hr className="flex-1 border-t border-ui-hairline-soft" />
          </div>

          <div className="bg-ui-primary-light rounded-sm border border-ui-hairline p-4">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="max-w-10 w-10 h-10 bg-ui-primary rounded-full flex items-center justify-center text-white shrink-0">
                  <Zap size={18} />
                </div>
                <div className="flex flex-col gap-0.5">
                  <h3 className="text-xs font-bold text-ui-ink">Automate conversations when someone clicks your link</h3>
                  <p className="text-[11px] text-ui-muted">TheQuickAssist handles instant auto-replies, lead capture, and full WhatsApp CRM.</p>
                </div>
              </div>

              <Button
                size="sm"
                className="flex items-center gap-2 font-semibold"
              >
                Start free <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
